import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useCreateFeed, useUpdateFeed } from '@/hooks/calendar-sync';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { useClient, useClients } from '@/hooks/clients';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { MAX_PAGE_SIZE } from '@shared/schemas/pagination';
import type { CalendarFeedView } from '@shared/schemas/calendar-sync';

/*
 * Add or edit a calendar feed — ROADMAP.md §11.7.
 *
 * The client list is **high-priority only**, because that is who the feature
 * is for (business-rules.md §6) — the server refuses anybody else, and a
 * dropdown that offers people the save will refuse is a form that argues with
 * itself.
 *
 * The URL is the customer's booking calendar and pasting it here is the one
 * moment it crosses a screen. It is shown in this dialog (the admin owns it
 * and may need to fix a typo) and nowhere else in the app.
 */

export function FeedDialog({
  open,
  onOpenChange,
  feed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feed?: CalendarFeedView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <FeedForm
            feed={feed}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FeedForm({ feed, onDone }: { feed?: CalendarFeedView; onDone: () => void }) {
  const toast = useToast();
  const isEdit = feed !== undefined;

  const [clientSearch, setClientSearch] = useState('');
  const [clientId, setClientId] = useState(feed?.clientId ?? '');
  const [propertyId, setPropertyId] = useState(feed?.propertyId ?? '');
  const [name, setName] = useState(feed?.name ?? '');
  const [url, setUrl] = useState(feed?.url ?? '');
  const [serviceTypeId, setServiceTypeId] = useState(feed?.serviceTypeId ?? '');
  const [autoAssign, setAutoAssign] = useState(feed?.autoAssign ?? false);
  const [duration, setDuration] = useState(
    feed?.defaultDurationMinutes === null || feed?.defaultDurationMinutes === undefined
      ? ''
      : String(feed.defaultDurationMinutes),
  );

  const clients = useClients({
    sort: 'name',
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: clientSearch,
    activity: 'active',
    // §6's gate, reflected in the dropdown rather than discovered on save.
    highPriorityOnly: true,
  });
  const clientDetail = useClient(clientId === '' ? undefined : clientId);
  const services = useServiceTypes({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: '',
    activity: 'active',
  });

  const create = useCreateFeed();
  const update = useUpdateFeed(feed?.id ?? '');
  const mutation = isEdit ? update : create;
  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  const properties = (clientDetail.data?.properties ?? []).filter((entry) => entry.isActive);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const shared = {
      propertyId: propertyId === '' ? null : propertyId,
      name,
      url,
      serviceTypeId,
      autoAssign,
      // Blank means "use the rate card's", which is null on the wire — not
      // zero, and not the string.
      defaultDurationMinutes: duration === '' ? null : Number(duration),
    };

    const handlers = {
      onSuccess: (saved: CalendarFeedView) => {
        toast.success(
          isEdit
            ? `Saved ${saved.name}.`
            : // Says what happens next, so nobody sits waiting for bookings
              // that arrive on the worker's own clock.
              `Added. Bookings import within four hours — or press Sync now.`,
        );
        onDone();
      },
      onError: (mutationError: unknown) => {
        if (!(mutationError instanceof ApiError) || mutationError.fieldErrors === undefined) {
          toast.error('That could not be saved.');
        }
      },
    };

    if (isEdit) {
      update.mutate(shared, handlers);
    } else {
      create.mutate({ ...shared, clientId }, handlers);
    }
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${feed.name}` : 'Add a calendar feed'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        The iCal link from a listing’s calendar export. Checkouts become turnover cleans.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        {isEdit ? (
          <div className="bg-paper-50 rounded-sm border border-ink-300 px-3 py-2.5">
            <p className="text-label text-ink-900">{feed.clientName}</p>
            <p className="text-meta text-ink-500">
              A feed stays with its client — for another client’s listing, add a new feed.
            </p>
          </div>
        ) : (
          <>
            <Field
              label="Search high-priority clients"
              name="clientSearch"
              type="search"
              value={clientSearch}
              onChange={(event) => {
                setClientSearch(event.target.value);
              }}
              placeholder="Name, business, phone or email"
            />

            <SelectField
              label="Client"
              name="clientId"
              required
              value={clientId}
              onChange={(id) => {
                setClientId(id);
                setPropertyId('');
              }}
              hint="Only high-priority clients — that is who calendar sync is for."
              error={fieldErrors.clientId}
            >
              <option value="">Choose a client</option>
              {(clients.data?.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.displayName}
                </option>
              ))}
            </SelectField>
          </>
        )}

        <SelectField
          label="Property"
          name="propertyId"
          value={propertyId}
          onChange={setPropertyId}
          disabled={!isEdit && clientId === ''}
          hint="Bookings import either way, but no cleans are created until the feed has a property."
          error={fieldErrors.propertyId}
        >
          <option value="">
            {!isEdit && clientId === '' ? 'Choose a client first' : 'No property yet'}
          </option>
          {(isEdit && properties.length === 0 && feed.propertyId !== null
            ? [{ id: feed.propertyId, nickname: feed.propertyLabel, fullAddress: '' }]
            : properties
          ).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.nickname
                ? entry.fullAddress
                  ? `${entry.nickname} — ${entry.fullAddress}`
                  : entry.nickname
                : entry.fullAddress}
            </option>
          ))}
        </SelectField>

        <Field
          label="Feed name"
          name="name"
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          placeholder="e.g. The A-Frame on Airbnb"
          error={fieldErrors.name}
        />

        <Field
          label="iCal URL"
          name="url"
          type="url"
          required
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
          }}
          placeholder="https://…/listing.ics"
          hint="Treat it like a password — anyone holding it can read this calendar."
          error={fieldErrors.url}
        />

        <SelectField
          label="Turnovers are billed as"
          name="serviceTypeId"
          required
          value={serviceTypeId}
          onChange={setServiceTypeId}
          hint="From the rate card. Add a Turnover service there first if you want one."
          error={fieldErrors.serviceTypeId}
        >
          <option value="">Choose a service</option>
          {(services.data?.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </SelectField>

        <Field
          label="How long a turnover takes here"
          name="defaultDurationMinutes"
          type="number"
          inputMode="numeric"
          min={15}
          max={1440}
          value={duration}
          onChange={(event) => {
            setDuration(event.target.value);
          }}
          hint="Minutes. Leave blank to use the rate card's own figure for this service."
          error={fieldErrors.defaultDurationMinutes}
        />

        {/*
          **Suggests a cleaner; it does not commit to one.** It runs the same
          ranking the assignment dialog runs and takes the best person it does
          not disqualify — and where there is nobody, the turnover is still
          created with an empty crew, because a job nobody can cover is something
          the office needs to see on the calendar (business-rules.md §4).
        */}
        <label className="flex min-h-touch items-start gap-2.5">
          <input
            type="checkbox"
            name="autoAssign"
            checked={autoAssign}
            onChange={(event) => {
              setAutoAssign(event.target.checked);
            }}
            className="mt-1 size-4 rounded-sm border-ink-300"
          />
          <span>
            <span className="block text-label text-ink-900">
              Put a cleaner on imported turnovers
            </span>
            <span className="block text-meta text-ink-500">
              Picks the best available person the same way the assignment screen ranks them. If
              nobody is free the clean is still created, with nobody on it.
            </span>
          </span>
        </label>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add feed'}
          </Button>
        </div>
      </form>
    </>
  );
}

/** A labelled `<select>` — the same shape `Field` gives an `<input>`. */
function SelectField({
  label,
  name,
  value,
  onChange,
  children,
  required,
  disabled,
  hint,
  error,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  error?: string;
}) {
  const id = `feed-${name}`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <select
        id={id}
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={cn(
          'min-h-control w-full rounded-sm border bg-white px-3 text-body',
          'disabled:bg-paper-50 disabled:text-ink-500',
          error ? 'border-status-priority-text' : 'border-ink-300',
        )}
      >
        {children}
      </select>
      {hint ? <p className="text-meta text-ink-500">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-meta text-status-priority-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
